import axiosInstance from '../api';

// The two AI-governance frameworks seeded by PRD-74 (platform changelogs A1/A2).
export const AI_FRAMEWORKS = [
  { id: 9, shortName: 'ISO 42001', displayName: 'ISO/IEC 42001' },
  { id: 10, shortName: 'NIST AI RMF', displayName: 'NIST AI RMF' },
];
export const AI_FRAMEWORK_IDS = AI_FRAMEWORKS.map((f) => f.id);

// ── Readiness + crosswalk (reuses the existing /api/compliance engine) ──
export const getFrameworksProgress = () =>
  axiosInstance.get('/compliance/frameworks').then((r) => r.data || []);

// Resolve the AI-governance frameworks (ISO 42001 / NIST AI RMF) to their REAL ids for
// THIS org/env by matching names in the live compliance catalog — instead of trusting the
// hardcoded seed ids (9/10), which differ across orgs and made the workspace appear "not
// accessible". Returns [] when the org simply has no AI framework (a clean not-enrolled
// signal); rejects only on a real network/auth failure so the UI can tell the two apart.
const AI_FRAMEWORK_RX = /(42001|ai\s*rmf|ai\s*risk\s*management)/i;
export const resolveAiFrameworks = () =>
  getFrameworksProgress().then((list) =>
    (list || [])
      // The /compliance/frameworks payload keys id/name as frameworkId/frameworkName —
      // read either shape, or getSoa is called with an undefined frameworkId and 500s.
      .map((f) => ({
        id: f.id ?? f.frameworkId,
        shortName: f.shortName || f.name || f.frameworkName,
        displayName: f.displayName || f.frameworkName || f.name,
      }))
      .filter((f) => f.id != null && AI_FRAMEWORK_RX.test(`${f.displayName || ''} ${f.shortName || ''}`))
  );

export const getCrossFrameworkOpportunity = () =>
  axiosInstance.get('/compliance/cross-framework-opportunity').then((r) => r.data || []);

export const getFrameworkProgress = (id) =>
  axiosInstance.get(`/compliance/frameworks/${id}/progress`).then((r) => r.data);

// ── AI asset inventory (PRD-74 C2) ──
export const listAiAssets = (params = {}) =>
  axiosInstance.get('/iasm/assets', { params: { layer: 'AI', size: 200, ...params } }).then((r) => r.data);

export const getAssetReviewQueue = (params = {}) =>
  axiosInstance.get('/iasm/assets/review-queue', { params }).then((r) => r.data);

export const createAiAsset = (body) => axiosInstance.post('/iasm/assets', body).then((r) => r.data);
export const updateAiAsset = (id, body) => axiosInstance.put(`/iasm/assets/${id}`, body).then((r) => r.data);
export const retireAiAsset = (id) => axiosInstance.delete(`/iasm/assets/${id}`).then((r) => r.data);
export const reviewAiAsset = (id, confirm) =>
  axiosInstance.patch(`/iasm/assets/${id}/review`, { confirm }).then((r) => r.data);

// ── Artifacts (PRD-74 D1/D2) ──
export const getSoa = (frameworkId) =>
  axiosInstance.get('/ai-gov/soa', { params: { frameworkId } }).then((r) => r.data);

export const setSoaApplicability = (body) =>
  axiosInstance.post('/ai-gov/soa/applicability', body).then((r) => r.data);

export const listRisks = () => axiosInstance.get('/ai-gov/risk-register').then((r) => r.data || []);
export const createRisk = (body) => axiosInstance.post('/ai-gov/risk-register', body).then((r) => r.data);
export const updateRisk = (id, body) => axiosInstance.put(`/ai-gov/risk-register/${id}`, body).then((r) => r.data);
export const deleteRisk = (id) => axiosInstance.delete(`/ai-gov/risk-register/${id}`).then((r) => r.data);
export const generateRisks = (frameworkId) =>
  axiosInstance.post('/ai-gov/risk-register/generate', null, { params: { frameworkId } }).then((r) => r.data);

// ── Scope statement + model cards (PRD-74 D3) ──
export const getScopeStatement = (frameworkId) =>
  axiosInstance.get('/ai-gov/scope-statement', { params: { frameworkId } }).then((r) => r.data);

export const getModelCards = () => axiosInstance.get('/ai-gov/model-cards').then((r) => r.data || []);
export const getModelCard = (assetId) => axiosInstance.get(`/ai-gov/model-card/${assetId}`).then((r) => r.data);

// Persist editable model-card narrative under asset metadata.modelCard (reuses the asset update API).
export const saveModelCardNarrative = (assetId, currentMetadata, narrative) =>
  updateAiAsset(assetId, { metadata: { ...(currentMetadata || {}), modelCard: narrative } });

// ── Export (PRD-74 D4) — fetch as blob (auth header) and trigger a download ──
const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadBundle = (frameworkId) =>
  axiosInstance
    .get('/ai-gov/export/bundle', { params: { frameworkId }, responseType: 'blob' })
    .then((r) => triggerDownload(r.data, `ai-governance-bundle-${frameworkId}.zip`));

// ── Continuous control monitoring (PRD-74 §6.3) ──
export const getCcmStatus = () => axiosInstance.get('/ai-gov/ccm/status').then((r) => r.data);
export const getBiasTrend = (assetId) =>
  axiosInstance.get('/ai-gov/ccm/bias-metrics', { params: { assetId } }).then((r) => r.data || []);
export const ingestBiasMetric = (body) =>
  axiosInstance.post('/ai-gov/ccm/bias-metrics', body).then((r) => r.data);
// Run the three AI checks on demand and latch findings into the AI audit.
export const runCcmChecks = () => axiosInstance.post('/ai-gov/ccm/run').then((r) => r.data);

// ── AIMS documents: generate + finalize (IN_FORCE) from the UI ──
export const listAimsDocuments = () => axiosInstance.get('/ai-gov/documents').then((r) => r.data || []);
export const adoptAimsDocument = (docType, frameworkId) =>
  axiosInstance.post('/ai-gov/documents/adopt', null, { params: { docType, frameworkId } }).then((r) => r.data);

export const openHtmlReport = (frameworkId) =>
  axiosInstance
    .get('/ai-gov/export/html', { params: { frameworkId }, responseType: 'blob' })
    .then((r) => {
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/html' }));
      window.open(url, '_blank');
    });
